"use client";
import { Article } from "@/app/types/blogTypes";
import { cn } from "@/lib/utils";
import { calculateReadingTime } from "@/utils/calculateReadingTime";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  Calendar,
  EyeIcon,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useState, useEffect, useRef } from "react";

type ArticlesProps = {
  articles: Article[];
};

export const BlogTopArticles = ({ articles }: ArticlesProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [direction, setDirection] = useState<"next" | "prev">("next");

  useEffect(() => {
    setIsMounted(true);
    const updateSize = () => setIsDesktop(window.innerWidth >= 950);
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const goToPrevious = () => {
    setDirection("prev");
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? articles.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setDirection("next");
    setCurrentIndex((prevIndex) =>
      prevIndex === articles.length - 1 ? 0 : prevIndex + 1
    );
  };

  const canGoNext = currentIndex < articles.length - 1;
  const canGoPrevious = currentIndex > 0;

  if (!isMounted) {
    return (
      <div className="w-full px-6 pt-10 md:pt-24 md:px-[120px] md:items-center md:justify-center md:flex md:flex-col">
        <div className="w-full md:max-w-[1200px]">
          <h2 className="text-xl text-[#1A1A1A] leading-6 text-left">
            Artigos
          </h2>
        </div>
      </div>
    );
  }

  if (isDesktop) {
    return (
      <div className="flex flex-col justify-center items-center w-full max-w-[1200px] mx-auto px-6 mt-10 ">
        <div className="w-full md:max-w-[1200px]">
          <h2 className="text-xl text-[#1A1A1A] leading-6 text-left">
            Artigos
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 w-full max-w-[1200px] mx-auto px-6 md:px-0 ">
          {articles.map((article, index) => (
            <BlogArticleCard key={index} article={article} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-6 pt-14 flex-col">
      <div className="flex w-full justify-between items-center">
        <h2 className="text-xl text-[#1A1A1A] leading-6">Artigos</h2>
        <div className="flex items-center gap-2">
          <button
            className={cn(
              "flex size-10 border   rounded-full items-center justify-center",
              canGoPrevious ? "border-brand-dark" : "border-gray-300"
            )}
            onClick={goToPrevious}
            disabled={!canGoPrevious}
          >
            <ChevronLeft
              size={18}
              className={canGoPrevious ? "text-brand-dark" : "text-gray-300"}
            />
          </button>
          <button
            className={cn(
              "flex size-10 border   rounded-full items-center justify-center",
              canGoNext ? "border-brand-dark" : "border-gray-300"
            )}
            disabled={!canGoNext}
            onClick={goToNext}
          >
            <ChevronRight
              size={18}
              className={canGoNext ? "text-brand-dark" : "text-gray-300"}
            />
          </button>
        </div>
      </div>
      <div className="relative mt-6 overflow-hidden min-h-[336px] rounded-[32px]">
        <AnimatePresence initial={false}>
          <motion.div
            key={currentIndex}
            className="absolute w-full h-full overflow-hidden rounded-[32px]"
            initial={{
              opacity: 0,
              x: direction === "next" ? 200 : -200,
            }}
            animate={{
              opacity: 1,
              x: 0,
            }}
            exit={{
              opacity: 0,
              x: direction === "next" ? -200 : 200,
            }}
            transition={{
              duration: 0.4,
              ease: "easeInOut",
            }}
          >
            <BlogArticleCard article={articles[currentIndex]} />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export const BlogArticleCard = ({ article }: { article: Article }) => {
  return (
    <Link href={`/blog/${article.slug}`}>
      <div className="relative w-full h-[336px] flex  aspect-video overflow-hidden rounded-[20px] shadow-md">
        <Image
          src={`${article?.cover?.url}`}
          alt={`${article?.cover?.alternativeText}`}
          width={1200}
          height={600}
          priority
          className="object-cover w-full h-full rounded-[20px]"
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        />
        <div className="absolute bottom-0 left-0 w-full h-full bg-gradient-to-t from-[#00000082] to-transparent z-10 rounded-b-[20px]"></div>
        <div className="absolute items-start justify-end flex-col bottom-0 left-0 w-full h-1/2 z-20 flex px-6 pb-6 rounded-b-[20px]">
          <div className="mb-3 flex items-center gap-3">
            {article?.tags?.map((tag) => {
              return (
                <div
                  key={tag.id}
                  className="rounded-full items-center gap-1 flex justify-center text-xs py-1 px-2 font-bold"
                  style={{
                    backgroundColor: tag?.color?.background,
                    color: tag?.color?.text,
                  }}
                >
                  {tag?.icon && (
                    <Image
                      src={tag.icon[0].url}
                      alt=""
                      width={12}
                      height={12}
                    />
                  )}
                  {tag?.name}
                </div>
              );
            })}
          </div>

          <h2 className="w-full text-white font-lexend leading-7 text-[20px] text-2xl font-normal line-clamp-2">
            {article?.title}
          </h2>
          <div className="flex md:flex-row flex-col w-full items-start md:items-center justify-between gap-2 mt-3">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <Calendar size={16} className="text-white" />
                <p className="text-white text-sm md:text-base">
                  {new Date(article.publishedAt).toLocaleDateString("pt-BR", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })}
                </p>
              </div>
              <p className="text-white text-sm md:text-base">|</p>
              <div className=" flex items-center gap-2">
                <EyeIcon size={16} className="text-white" />
                <p className="text-white text-sm md:text-base">
                  {article.readingTimeNew} Minutos
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-white text-sm md:text-base">
                Leia mais sobre
              </span>
              <ArrowRight size={20} color="#fff" />
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};
